import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './PurchaseOrderDetail.css';
import PurchaseOrderReceipt from '../../../CommonComponents/Invoice Receipts/PurchaseOrderReceipt';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../UserAccounts/AuthProvider';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaTruck,
  FaUser,
  FaPen,
  FaPrint,
  FaTimes,
  FaPlus,
  FaTrash,
  FaUpload,
  FaCalculator,
  FaEye,
  FaCapsules,
  FaBarcode,
  FaBoxOpen,
  FaBoxes,
  FaGift,
  FaArrowUp,
  FaTag,
  FaPercent,
  FaReceipt,
  FaCoins,
  FaCog,
  FaChevronDown,
  FaChevronRight,
  FaChartLine,
  FaBolt,
  FaInfoCircle,
  FaLock,
  FaFileInvoice,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';

/**
 * PurchaseOrderDetail Component
 * Viswateja Hospitals - Pharmacy Purchase Order Details & Item Management
 * 
 * Recreated with 100% precision from reference design + VT Hospitals Enterprise Quality:
 * 1. Top Bar: Back navigation + Purchase Order number + Live Status badge.
 * 2. 3 Header Summary Cards:
 *    - Card 1: Total Items Stat Card with cyan accent & centered large counter.
 *    - Card 2: Supplier Information Card (GST, DL No 1 & 2, Phone) with quick edit.
 *    - Card 3: Created By / Order Metadata Card with Download & Print actions.
 * 3. Master Medicine Items Toolbar:
 *    - Fused Search (Brand / Molecule / Item selector + Search input + Reset).
 *    - "Ordered Items" quick pill button.
 *    - "Upload File" import action button.
 *    - "Select fields..." customization & Refresh.
 * 4. Master Items Data Table:
 *    - 12 Dedicated columns with icon headers (Item Name, Batch, Expiry, Pack Qty, Qty [Packs|Units], Free, MRP, Rate, Disc %, % GST, Amount, Actions).
 *    - Interactive Add Item / Inline Row creation with auto-calculations (Taxable, CGST, SGST, Total).
 * 5. GST Breakdown & Order Summary Footer:
 *    - Real-time GST Breakdown matrix (0%, 5%, 12%, 18%, 28%).
 *    - Add discount, roundoff, and adjustment helpers.
 *    - Subtotal, SGST, CGST, and Full Cyan Grand Total banner.
 *    - Action CTAs: Preview Purchase Order & Delete Order.
 */
import CreateNewItemModal from './CreateNewItemModal';
import MedicineBatchDetailModal from './MedicineBatchDetailModal';
import EditPurchaseSupplierModal from './EditPurchaseSupplierModal';
import { BillDiscountModal, RoundOffModal } from './BillAdjustmentModals';
import UploadItemsModal from './UploadItemsModal';

const PurchaseOrderDetail = ({
  order,
  onBack,
  onUpdate,
  suppliers = []
}) => {
  // ─── Order & Supplier State ────────────────────────────────────────────────
  const { userData } = useAuth();
  const currentUserName = userData?.name || userData?.username || userData?.fullName || localStorage.getItem('username') || order?.representativeName || 'Admin';

  const [currentOrder, setCurrentOrder] = useState(order || {});
  const [supplierDetails, setSupplierDetails] = useState(null);

  // ─── Items List State ─────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [quantityMode, setQuantityMode] = useState('units'); // 'packs' | 'units'

  // ─── Search & Filter State ────────────────────────────────────────────────
  const [searchCategory, setSearchCategory] = useState('Brand');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState('ordered'); // 'ordered' | 'all'
  const searchWrapperRef = useRef(null);

  // ─── Create New Item Modal State ──────────────────────────────────────────
  const [isCreateItemModalOpen, setIsCreateItemModalOpen] = useState(false);

  // ─── Edit Supplier Modal State (Only Supplier Name Editable) ─────────────
  const [isEditSupplierModalOpen, setIsEditSupplierModalOpen] = useState(false);

  // ─── Medicine Batch Detail Confirmation Modal State ───────────────────────
  const [isBatchDetailModalOpen, setIsBatchDetailModalOpen] = useState(false);
  const [selectedMedicineForBatch, setSelectedMedicineForBatch] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  // ─── Database Medicine Search State ───────────────────────────────────────
  const [dbMedicines, setDbMedicines] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // ─── Bill Discount & Round Off Modals State ───────────────────────────────
  const [discountAmount, setDiscountAmount] = useState(0); // Treated as Bill Discount
  const [roundoffAmount, setRoundoffAmount] = useState(0);
  const [manualRoundoffDisabled, setManualRoundoffDisabled] = useState(false);
  const [isBillDiscountModalOpen, setIsBillDiscountModalOpen] = useState(false);
  const [isRoundOffModalOpen, setIsRoundOffModalOpen] = useState(false);
  const [isAdjustmentInfoModalOpen, setIsAdjustmentInfoModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // ─── Status Update State & Handler ────────────────────────────────────────
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusAlertMessage, setStatusAlertMessage] = useState('');

  // Helper to load complete purchase order and item response into component state
  const applyEntryDataToState = (entryData) => {
    if (!entryData) return;
    setCurrentOrder(entryData);
    if (entryData.discountAmount != null) setDiscountAmount(Number(entryData.discountAmount));
    if (entryData.roundoffAmount != null) setRoundoffAmount(Number(entryData.roundoffAmount));
    if (Array.isArray(entryData.items)) {
      setItems(
        entryData.items.map((it) => {
          const stock = it.stockQuantity != null ? Number(it.stockQuantity) : (it.availableUnits != null ? Number(it.availableUnits) : 0);
          const packQty = it.packQuantity || 10;
          return {
            id: it.id,
            medicineId: it.medicineId,
            itemName: it.itemName,
            brand: it.brand,
            composition: it.composition,
            hsn: it.hsn,
            batchNo: it.batchNo,
            expiryDate: it.expiryDate,
            packType: it.packType,
            packQuantity: packQty,
            quantity: it.quantity || 1,
            freeQuantity: it.freeQuantity || 0,
            mrp: Number(it.mrp) || 0,
            rate: Number(it.rate) || 0,
            packMrpDiscount: Number(it.packMrpDiscount) || 0,
            discountPercent: Number(it.discountPercent) || 0,
            gstPercent: Number(it.gstPercent) || 12,
            rack: it.rack,
            stockQuantity: stock,
            availableUnits: stock,
            availableQuantity: stock,
            availablePacks: packQty > 0 ? Math.floor(stock / packQty) : 0,
            stockBalance: stock
          };
        })
      );
    }
  };

  const fetchLatestOrderDetails = async (idToFetch = currentOrder?.id) => {
    if (!idToFetch) return;
    try {
      const res = await axios.get(ENDPOINTS.GET_PURCHASE_ENTRY_BY_ID(idToFetch));
      if (res.data) {
        applyEntryDataToState(res.data);
      }
    } catch (err) {
      console.warn('Could not fetch latest purchase order details:', err);
    }
  };

  const promptDeleteConfirmation = () => {
    toast.dismiss('confirm-delete-order-status');

    toast.warn(
      ({ closeToast }) => (
        <div className="vt-po-delete-confirm-toast">
          <div className="vt-po-delete-toast-header">
            <span className="vt-po-delete-toast-badge">
              <FaExclamationTriangle size={13} style={{ marginRight: '5px' }} />
              Confirm Action
            </span>
            <button
              type="button"
              className="vt-po-delete-toast-x"
              onClick={closeToast}
              title="Close"
            >
              ×
            </button>
          </div>
          <div className="vt-po-delete-toast-body">
            <p className="vt-po-delete-toast-main">
              Are you sure you want to mark this purchase order as <strong>DELETED</strong>?
            </p>
            <p className="vt-po-delete-toast-sub">
              If stock was already added, it will be deducted from inventory.
            </p>
          </div>
          <div className="vt-po-delete-toast-footer">
            <button
              type="button"
              className="vt-po-toast-btn-cancel"
              onClick={closeToast}
            >
              Cancel
            </button>
            <button
              type="button"
              className="vt-po-toast-btn-confirm"
              onClick={async () => {
                closeToast();
                await executeStatusChange('DELETED');
              }}
            >
              Yes, Mark as DELETED
            </button>
          </div>
        </div>
      ),
      {
        toastId: 'confirm-delete-order-status',
        position: 'top-center',
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        className: 'vt-po-interactive-toast'
      }
    );
  };

  const handleStatusChange = (newStatus) => {
    if (!currentOrder?.id) return;
    if (newStatus === currentOrder.status) return;

    if (newStatus === 'DELETED') {
      promptDeleteConfirmation();
      return;
    }

    executeStatusChange(newStatus);
  };

  const executeStatusChange = async (newStatus) => {
    if (!currentOrder?.id) return;
    if (newStatus === currentOrder.status) return;

    setIsUpdatingStatus(true);
    const operatorName = userData?.name || userData?.username || userData?.fullName || localStorage.getItem('username') || currentOrder?.representativeName || 'Admin';
    const nowIso = new Date().toISOString();

    const isProcessed = newStatus === 'PROCESSED_AND_RESTOCKED' || newStatus === 'RESTOCKED' || newStatus === 'PROCESSED';
    const isDeleted = newStatus === 'DELETED';

    try {
      const payload = {
        supplierId: currentOrder.supplierId,
        supplierName: currentOrder.supplierName,
        grnNo: currentOrder.grnNo,
        invoiceNumber: currentOrder.invoiceNumber,
        invoiceDate: currentOrder.invoiceDate,
        entryDate: currentOrder.entryDate,
        representativeName: currentOrder.representativeName,
        amount: currentOrder.amount,
        clearedAmount: currentOrder.clearedAmount,
        status: newStatus,
        entryType: currentOrder.entryType,
        notes: currentOrder.notes,
        ...(isProcessed ? { processedBy: operatorName, processedAt: nowIso } : {}),
        ...(isDeleted ? { deletedBy: operatorName, deletedAt: nowIso } : {})
      };

      const res = await axios.put(`${ENDPOINTS.CREATE_PURCHASE_ENTRY}/${currentOrder.id}`, payload);
      const updated = res.data;

      const updatedAudit = {
        ...(updated || {}),
        status: newStatus,
        ...(isProcessed ? { processedBy: updated?.processedBy || operatorName, processedAt: updated?.processedAt || nowIso } : {}),
        ...(isDeleted ? { deletedBy: updated?.deletedBy || operatorName, deletedAt: updated?.deletedAt || nowIso } : {})
      };

      setCurrentOrder((prev) => ({
        ...prev,
        ...updatedAudit
      }));

      if (updated && Array.isArray(updated.items) && updated.items.length > 0) {
        applyEntryDataToState({
          ...updated,
          ...updatedAudit
        });
      } else {
        await fetchLatestOrderDetails(currentOrder.id);
      }

      // Refresh database medicine search list so suggestions show updated available units
      axios
        .get(ENDPOINTS.SEARCH_MEDICINES, {
          params: {
            query: searchQuery.trim(),
            category: searchCategory
          }
        })
        .then((sRes) => {
          if (Array.isArray(sRes.data)) setDbMedicines(sRes.data);
        })
        .catch(() => {});

      if (onUpdate) {
        onUpdate({
          ...currentOrder,
          ...updatedAudit
        });
      }

      if (isProcessed) {
        setStatusAlertMessage('✓ Status set to PROCESSED AND RESTOCKED. Stock added to inventory.');
        toast.success('Order status updated to PROCESSED AND RESTOCKED. Stock added to inventory.');
      } else if (isDeleted) {
        setStatusAlertMessage(`✓ Status set to DELETED.`);
        toast.success('Order status updated to DELETED. Stock deducted from inventory if previously added.');
      } else {
        setStatusAlertMessage(`✓ Status set to ${newStatus}.`);
        toast.success(`Order status updated to ${newStatus}`);
      }
      setTimeout(() => setStatusAlertMessage(''), 4500);
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update status: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ─── Save Order Status ────────────────────────────────────────────────────
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');

  // ─── Print / Preview Modal State ──────────────────────────────────────────
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // ─── Selected Row IDs for Bulk Actions (e.g. Delete Rows) ─────────────────
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  // ─── Order Invoiced Status Check ──────────────────────────────────────────
  // Adding medicines can only be done when status is INVOICED.
  // When PROCESSED AND RESTOCKED or DELETED, items cannot be added/modified.
  const isOrderInvoiced = useMemo(() => {
    const st = String(currentOrder?.status || 'INVOICED').toUpperCase().replace(/\s+/g, '_');
    return st === 'INVOICED';
  }, [currentOrder?.status]);

  // ─── Edit Invoice Modal State & Handlers ──────────────────────────────────
  const [isEditInvoiceModalOpen, setIsEditInvoiceModalOpen] = useState(false);
  const [editInvoiceForm, setEditInvoiceForm] = useState({
    invoiceNumber: '',
    invoiceDate: ''
  });
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);

  const handleOpenEditInvoiceModal = () => {
    setEditInvoiceForm({
      invoiceNumber: currentOrder.invoiceNumber || '',
      invoiceDate: currentOrder.invoiceDate ? currentOrder.invoiceDate.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setIsEditInvoiceModalOpen(true);
  };

  const handleSaveInvoiceDetails = async (e) => {
    e.preventDefault();
    if (!editInvoiceForm.invoiceNumber.trim()) {
      toast.warning('Invoice Number cannot be empty.');
      return;
    }
    if (!editInvoiceForm.invoiceDate) {
      toast.warning('Please select an Invoice Date.');
      return;
    }

    setIsSavingInvoice(true);
    try {
      const payload = {
        invoiceNumber: editInvoiceForm.invoiceNumber.trim(),
        invoiceDate: editInvoiceForm.invoiceDate
      };

      await axios.put(`${ENDPOINTS.CREATE_PURCHASE_ENTRY}/${currentOrder.id}`, payload);

      setCurrentOrder((prev) => ({
        ...prev,
        invoiceNumber: payload.invoiceNumber,
        invoiceDate: payload.invoiceDate
      }));

      setIsEditInvoiceModalOpen(false);
      toast.success('Invoice details updated successfully.');
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Failed to update invoice details:', err);
      toast.error('Failed to update invoice details: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSavingInvoice(false);
    }
  };

  // Close suggestions popover on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Live Backend Search Effect (with Debounce) ───────────────────────────
  useEffect(() => {
    let isCancelled = false;
    const timer = setTimeout(() => {
      setIsSearching(true);
      axios
        .get(ENDPOINTS.SEARCH_MEDICINES, {
          params: {
            query: searchQuery.trim(),
            category: searchCategory
          }
        })
        .then((res) => {
          if (!isCancelled && Array.isArray(res.data)) {
            setDbMedicines(res.data);
          }
        })
        .catch((err) => {
          console.warn('Live search fallback:', err);
        })
        .finally(() => {
          if (!isCancelled) setIsSearching(false);
        });
    }, 200);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, searchCategory]);

  // ─── Load Existing Purchase Entry & Items from Backend ────────────────────
  useEffect(() => {
    if (order?.id) {
      fetchLatestOrderDetails(order.id);
    }
  }, [order?.id]);

  // ─── Filtered Suggestions (Database Medicines Only) ─────────────────────
  const filteredSuggestions = useMemo(() => {
    if (dbMedicines && dbMedicines.length > 0) {
      return dbMedicines.map((m) => {
        const stock = m.stockQuantity != null ? Number(m.stockQuantity) : 0;
        const packQty = m.packQuantity || 10;
        const packs = packQty > 0 ? Math.floor(stock / packQty) : 0;
        return {
          id: m.medicineId || m.id,
          medicineId: m.medicineId,
          itemName: m.medicineBrand || '',
          rawName: m.medicineBrand || '',
          extension: m.extension || '',
          itemType: m.itemType || 'TABLET',
          packType: m.packType || 'strip',
          category: m.category || 'MEDICINES',
          scheduleType: m.scheduleType || 'Schedule H',
          brand: m.medicineBrand || 'Generic',
          composition: m.composition || '',
          hsn: m.hsnCode || '300490',
          hsnCode: m.hsnCode || '300490',
          packQuantity: packQty,
          batchNo: m.batchNumber || '',
          expiryDate: m.expiryDate || '2029-02-28',
          mrp: m.packMrp || m.medicinePrice || 0,
          rate: m.packRate || 0,
          discountPercent: 0,
          gstPercent: m.gstPercent || 12,
          rack: m.rack || '',
          stockQuantity: stock,
          availableUnits: stock,
          availableQuantity: stock,
          availablePacks: packs,
          stockBalance: stock
        };
      });
    }
    return [];
  }, [dbMedicines]);

  // ─── Open Batch Detail Popup on Selecting Suggestion ──────────────────────
  const handleSelectSuggestion = (med) => {
    setEditingItemIndex(null);
    setEditingItemId(null);
    setSelectedMedicineForBatch(med);
    setIsBatchDetailModalOpen(true);
    setShowSuggestions(false);
  };

  // ─── Auto-Save / Sync Purchase Order Items to DB in Real-Time ────────────
  const syncItemsToDb = async (updatedItems, extraOverrides = {}) => {
    if (!currentOrder?.id) return;
    setIsSavingOrder(true);
    try {
      const payload = {
        discountAmount: extraOverrides.discountAmount !== undefined ? extraOverrides.discountAmount : (discountAmount || 0),
        adjustmentAmount: extraOverrides.adjustmentAmount !== undefined ? extraOverrides.adjustmentAmount : 0,
        roundoffAmount: extraOverrides.roundoffAmount !== undefined ? extraOverrides.roundoffAmount : (roundoffAmount || 0),
        items: updatedItems.map((it) => ({
          id: typeof it.id === 'number' && it.id > 1000000000 ? null : it.id,
          medicineId: it.medicineId || null,
          itemName: it.itemName,
          brand: it.brand,
          composition: it.composition,
          hsn: it.hsn,
          batchNo: it.batchNo,
          expiryDate: it.expiryDate && it.expiryDate.length === 7 ? `${it.expiryDate}-28` : (it.expiryDate || null),
          packType: it.packType,
          packQuantity: it.packQuantity || 10,
          quantity: it.quantity || 1,
          freeQuantity: it.freeQuantity || 0,
          mrp: it.mrp || 0,
          rate: it.rate || 0,
          packMrpDiscount: it.packMrpDiscount || 0,
          discountPercent: it.discountPercent || 0,
          gstPercent: it.gstPercent || 12,
          rack: it.rack
        }))
      };

      const res = await axios.put(ENDPOINTS.SYNC_PURCHASE_ENTRY_ITEMS(currentOrder.id), payload);
      if (res.data) {
        setCurrentOrder(res.data);
        if (onUpdate) {
          onUpdate(res.data);
        }
        if (Array.isArray(res.data.items)) {
          setItems(
            res.data.items.map((it) => {
              const stock = it.stockQuantity != null ? Number(it.stockQuantity) : (it.availableUnits != null ? Number(it.availableUnits) : 0);
              const packQty = it.packQuantity || 10;
              return {
                id: it.id,
                medicineId: it.medicineId,
                itemName: it.itemName,
                brand: it.brand,
                composition: it.composition,
                hsn: it.hsn,
                batchNo: it.batchNo,
                expiryDate: it.expiryDate,
                packType: it.packType,
                packQuantity: packQty,
                quantity: it.quantity || 1,
                freeQuantity: it.freeQuantity || 0,
                mrp: Number(it.mrp) || 0,
                rate: Number(it.rate) || 0,
                packMrpDiscount: Number(it.packMrpDiscount) || 0,
                discountPercent: Number(it.discountPercent) || 0,
                gstPercent: Number(it.gstPercent) || 12,
                rack: it.rack,
                stockQuantity: stock,
                availableUnits: stock,
                availableQuantity: stock,
                availablePacks: packQty > 0 ? Math.floor(stock / packQty) : 0,
                stockBalance: stock
              };
            })
          );
        }
        setSaveSuccessMessage('Saved to database');
        setTimeout(() => setSaveSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to auto-save purchase order items to DB:', err);
    } finally {
      setIsSavingOrder(false);
    }
  };

  // ─── Confirm Batch Detail Popup -> Populate to Table & Save to DB ─────────
  const handleConfirmBatchDetail = async (configuredItem) => {
    let nextItems = [];
    if (editingItemIndex !== null && editingItemIndex > -1) {
      nextItems = items.map((it, idx) => {
        if (idx === editingItemIndex) {
          return {
            ...it,
            ...configuredItem,
            id: it.id || configuredItem.id,
            medicineId: it.medicineId || configuredItem.medicineId,
            quantity: Number(configuredItem.quantity) || 1,
            freeQuantity: Number(configuredItem.freeQuantity) || 0,
            rate: configuredItem.rate,
            packRate: configuredItem.packRate,
            mrp: configuredItem.mrp,
            packMrp: configuredItem.packMrp,
            ratePerUnit: configuredItem.ratePerUnit,
            mrpPerUnit: configuredItem.mrpPerUnit,
            discountPercent: configuredItem.discountPercent,
            discountValue: configuredItem.discountValue,
            discountType: configuredItem.discountType,
            packMrpDiscount: configuredItem.packMrpDiscount,
            packMrpDiscountType: configuredItem.packMrpDiscountType,
            sellingMrp: configuredItem.sellingMrp,
            gstPercent: configuredItem.gstPercent,
            expiryDate: configuredItem.expiryDate,
            batchNo: configuredItem.batchNo,
            itemName: configuredItem.itemName,
            brand: configuredItem.brand,
            composition: configuredItem.composition,
            hsn: configuredItem.hsn,
            packQuantity: configuredItem.packQuantity,
            rack: configuredItem.rack,
            stockQuantity: configuredItem.stockQuantity != null ? configuredItem.stockQuantity : it.stockQuantity,
            availableUnits: configuredItem.availableUnits != null ? configuredItem.availableUnits : it.availableUnits,
            availableQuantity: configuredItem.availableQuantity != null ? configuredItem.availableQuantity : it.availableQuantity,
            availablePacks: configuredItem.availablePacks != null ? configuredItem.availablePacks : it.availablePacks,
            stockBalance: configuredItem.stockBalance != null ? configuredItem.stockBalance : it.stockBalance
          };
        }
        return it;
      });
      setEditingItemIndex(null);
      setEditingItemId(null);
    } else {
      const existingIndex = items.findIndex(
        (it) => it.itemName === configuredItem.itemName && it.batchNo === configuredItem.batchNo
      );
      if (existingIndex > -1) {
        nextItems = items.map((it, idx) =>
          idx === existingIndex
            ? {
              ...it,
              quantity: Number(it.quantity) + Number(configuredItem.quantity),
              freeQuantity: Number(it.freeQuantity || 0) + Number(configuredItem.freeQuantity || 0),
              rate: configuredItem.rate,
              packRate: configuredItem.packRate,
              mrp: configuredItem.mrp,
              packMrp: configuredItem.packMrp,
              discountPercent: configuredItem.discountPercent,
              gstPercent: configuredItem.gstPercent,
              expiryDate: configuredItem.expiryDate
            }
            : it
        );
      } else {
        nextItems = [...items, configuredItem];
      }
    }
    setItems(nextItems);
    setIsBatchDetailModalOpen(false);
    setSelectedMedicineForBatch(null);
    setSearchQuery('');
    await syncItemsToDb(nextItems);
  };

  const handleCreatedItemFromModal = async (createdItem) => {
    const nextItems = [...items, createdItem];
    setItems(nextItems);
    setSearchQuery('');
    setShowSuggestions(false);
    await syncItemsToDb(nextItems);
  };

  // ─── Resolve Supplier Details ─────────────────────────────────────────────
  useEffect(() => {
    const activeOrder = currentOrder?.id ? currentOrder : order;
    if (!activeOrder) return;

    const suppId = activeOrder.supplierId;
    const suppName = activeOrder.supplierName;

    const matchSupplier = (list) => {
      if (!list || !Array.isArray(list)) return null;
      if (suppId) {
        const byId = list.find((s) => String(s.id) === String(suppId));
        if (byId) return byId;
      }
      if (suppName) {
        const byName = list.find(
          (s) => String(s.name || '').trim().toLowerCase() === String(suppName).trim().toLowerCase()
        );
        if (byName) return byName;
      }
      return null;
    };

    const found = matchSupplier(suppliers);
    if (found && (found.dl1Number || found.dl2Number || found.phone1 || found.address)) {
      setSupplierDetails(found);
      return;
    }

    if (suppId) {
      axios
        .get(ENDPOINTS.GET_SUPPLIER_BY_ID(suppId))
        .then((res) => {
          if (res.data) setSupplierDetails(res.data);
        })
        .catch(() => {
          axios
            .get(ENDPOINTS.GET_ALL_SUPPLIERS, { params: { size: 500 } })
            .then((allRes) => {
              const list = Array.isArray(allRes.data) ? allRes.data : allRes.data?.content || [];
              const m = matchSupplier(list);
              if (m) setSupplierDetails(m);
            })
            .catch(() => {});
        });
    } else if (suppName) {
      axios
        .get(ENDPOINTS.GET_ALL_SUPPLIERS, { params: { size: 500 } })
        .then((allRes) => {
          const list = Array.isArray(allRes.data) ? allRes.data : allRes.data?.content || [];
          const m = matchSupplier(list);
          if (m) setSupplierDetails(m);
        })
        .catch(() => {});
    }
  }, [order, currentOrder?.supplierId, currentOrder?.supplierName, suppliers]);

  const handleSaveSupplier = async (selectedSupplier) => {
    try {
      if (!selectedSupplier || !selectedSupplier.name) return;

      setSupplierDetails(selectedSupplier);

      if (currentOrder?.id) {
        const payload = {
          ...currentOrder,
          supplierId: selectedSupplier.id || null,
          supplierName: selectedSupplier.name
        };
        await axios.put(`${ENDPOINTS.CREATE_PURCHASE_ENTRY}/${currentOrder.id}`, payload);
        setCurrentOrder((prev) => ({
          ...prev,
          supplierId: selectedSupplier.id || null,
          supplierName: selectedSupplier.name
        }));
      }

      setIsEditSupplierModalOpen(false);
      toast.success('Supplier updated successfully.');
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Failed to update purchase order supplier:', err);
      toast.error('Failed to update supplier: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleApplyBillDiscount = async (amount) => {
    setDiscountAmount(amount);
    await syncItemsToDb(items, { discountAmount: amount });
  };

  const handleApplyRoundOff = async (val) => {
    if (val === 0) {
      setManualRoundoffDisabled(true);
    } else {
      setManualRoundoffDisabled(false);
    }
    setRoundoffAmount(val);
    await syncItemsToDb(items, { roundoffAmount: val });
  };

  // ─── Format Date Helpers ──────────────────────────────────────────────────
  const formatDateHuman = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatExpiryMonthYear = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };

  const formatDateTimeHuman = (dateTimeStr) => {
    if (!dateTimeStr) {
      const now = new Date();
      return now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    const d = new Date(dateTimeStr);
    if (isNaN(d.getTime())) return dateTimeStr;
    return (
      d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' +
      d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(val || 0);
  };

  // ─── Calculations per Item ────────────────────────────────────────────────
  const calculateItemAmount = (item) => {
    const qty = Number(item.quantity) || 0;
    const packQty = Math.max(1, Number(item.packQuantity) || 1);

    // Rate is Rate / Unit (Rate per single unit/piece)
    const packRate = Number(item.packRate != null ? item.packRate : item.rate) || 0;
    const unitRate = packQty > 0 ? (packRate / packQty) : packRate;

    // Base amount = Total purchased units * Rate per unit = Qty (packs) * packRate
    const totalPurchasedUnits = qty * packQty;
    const baseAmount = qty * packRate;
    const gst = Number(item.gstPercent) || 0;

    let discountAmount = 0;
    let discPercent = 0;

    if (item.discountType === '₹' || item.supplierDiscountType === '₹') {
      const discVal = Number(item.discountValue != null ? item.discountValue : (item.discountAmount || item.discountPercent)) || 0;
      discountAmount = Math.min(baseAmount, qty * discVal);
      discPercent = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
    } else {
      discPercent = Number(item.discountPercent != null ? item.discountPercent : (item.discountValue || 0)) || 0;
      discountAmount = (baseAmount * discPercent) / 100;
    }

    const afterDiscount = Math.max(0, baseAmount - discountAmount);
    const gstVal = (afterDiscount * gst) / 100;
    const total = afterDiscount + gstVal;

    return {
      totalPurchasedUnits,
      unitRate,
      packRate,
      baseAmount,
      discountAmount,
      discountPercent: discPercent,
      taxableAmount: afterDiscount,
      gstAmount: gstVal,
      cgstAmount: gstVal / 2,
      sgstAmount: gstVal / 2,
      totalAmount: total
    };
  };

  // ─── Grand Totals & GST Summary ───────────────────────────────────────────
  const {
    grossBaseTotal,
    totalItemDiscount,
    subTotal,
    taxableSubTotal,
    totalSGST,
    totalCGST,
    gstBreakdown,
    netBeforeRoundOff,
    nearestInteger,
    autoRoundoffDiff,
    hasDecimals,
    effectiveRoundoff,
    billDiscountPct,
    grandTotal
  } = useMemo(() => {
    let gross = 0;
    let totalItemDisc = 0;
    let sub = 0;
    let sgst = 0;
    let cgst = 0;
    const breakdownMap = {};

    items.forEach((item) => {
      const calc = calculateItemAmount(item);
      gross += calc.baseAmount;
      totalItemDisc += calc.discountAmount;
      sub += calc.taxableAmount;
      sgst += calc.sgstAmount;
      cgst += calc.cgstAmount;

      const rateKey = Number(item.gstPercent) || 0;
      if (!breakdownMap[rateKey]) {
        breakdownMap[rateKey] = { rate: rateKey, subtotal: 0, taxable: 0, discount: 0, cgst: 0, sgst: 0, total: 0 };
      }
      breakdownMap[rateKey].subtotal += calc.baseAmount;
      breakdownMap[rateKey].taxable += calc.taxableAmount;
      breakdownMap[rateKey].discount += calc.discountAmount;
      breakdownMap[rateKey].cgst += calc.cgstAmount;
      breakdownMap[rateKey].sgst += calc.sgstAmount;
      breakdownMap[rateKey].total += calc.gstAmount;
    });

    // Net before round off: (Taxable subtotal + SGST + CGST) - Bill Discount
    const billDiscount = Number(discountAmount) || 0;
    const netBeforeRound = (sub + sgst + cgst) - billDiscount;
    const netRounded = Math.round(netBeforeRound * 100) / 100;

    // Nearest whole rupee calculation
    const nearest = Math.round(netRounded);
    const diff = Number((nearest - netRounded).toFixed(2));
    const decimalsExist = Math.abs(diff) > 0.0001;

    // AUTO-APPLY ROUND OFF:
    // If not manually disabled by the user, automatically apply diff
    let effectiveRoundoff = !manualRoundoffDisabled ? diff : (Number(roundoffAmount) || 0);

    const finalGrandTotal = Math.max(0, Number((netRounded + effectiveRoundoff).toFixed(2)));
    const billDiscPct = gross > 0 && billDiscount > 0 ? Math.round((billDiscount / gross) * 100) : 0;

    const list = Object.values(breakdownMap).sort((a, b) => a.rate - b.rate);
    const finalBreakdown = list.length > 0 ? list : [{ rate: 5, subtotal: 0, taxable: 0, discount: 0, cgst: 0, sgst: 0, total: 0 }];

    return {
      grossBaseTotal: gross,
      totalItemDiscount: totalItemDisc,
      subTotal: gross, // Sub total without GST and without discount
      taxableSubTotal: sub, // Amount after item discounts, before GST
      totalSGST: sgst,
      totalCGST: cgst,
      gstBreakdown: finalBreakdown,
      netBeforeRoundOff: netRounded,
      nearestInteger: nearest,
      autoRoundoffDiff: diff,
      hasDecimals: decimalsExist,
      effectiveRoundoff,
      billDiscountPct: billDiscPct,
      grandTotal: finalGrandTotal
    };
  }, [items, discountAmount, roundoffAmount, manualRoundoffDisabled]);

  // Auto-apply and auto-sync round off to state and database
  useEffect(() => {
    if (!manualRoundoffDisabled && roundoffAmount !== autoRoundoffDiff) {
      setRoundoffAmount(autoRoundoffDiff);
      if (currentOrder?.id) {
        syncItemsToDb(items, { roundoffAmount: autoRoundoffDiff });
      }
    }
  }, [autoRoundoffDiff, manualRoundoffDisabled, currentOrder?.id]);

  // ─── Row Selection & Bulk Deletion ───────────────────────────────────────
  const toggleSelectAll = () => {
    if (items.length === 0) return;
    if (selectedRowIds.length === items.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(items.map((it, idx) => (it.id != null ? it.id : `idx-${idx}`)));
    }
  };

  const toggleSelectRow = (identifier) => {
    setSelectedRowIds((prev) =>
      prev.includes(identifier)
        ? prev.filter((id) => id !== identifier)
        : [...prev, identifier]
    );
  };

  const handleDeleteRows = async () => {
    if (!isOrderInvoiced) {
      toast.warning('Modifying items is only allowed when order status is INVOICED.');
      return;
    }
    if (selectedRowIds.length === 0) {
      toast.info('Please select at least one row to delete.');
      return;
    }

    const count = selectedRowIds.length;
    const remainingItems = items.filter((it, idx) => {
      const rowId = it.id != null ? it.id : `idx-${idx}`;
      return !selectedRowIds.includes(rowId);
    });

    setItems(remainingItems);
    setSelectedRowIds([]);

    if (currentOrder?.id) {
      try {
        await syncItemsToDb(remainingItems);
        toast.success(`${count} row(s) deleted successfully.`);
      } catch (err) {
        console.error('Failed to sync after deleting rows', err);
        toast.error('Failed to delete rows: ' + (err.response?.data?.error || err.message));
      }
    } else {
      toast.success(`${count} row(s) deleted successfully.`);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!isOrderInvoiced) {
      toast.warning('Modifying items is only allowed when order status is INVOICED.');
      return;
    }
    const nextItems = items.filter((i) => i.id !== id);
    setItems(nextItems);
    setSelectedRowIds((prev) => prev.filter((rowId) => rowId !== id));
    if (currentOrder?.id) {
      if (typeof id === 'number' && id < 1000000000) {
        try {
          await axios.delete(ENDPOINTS.DELETE_PURCHASE_ENTRY_ITEM(currentOrder.id, id));
        } catch (err) {
          console.warn('Individual item delete failed, falling back to sync', err);
          await syncItemsToDb(nextItems);
          toast.success('Item removed successfully.');
          return;
        }
      }
      await syncItemsToDb(nextItems);
    }
    toast.success('Item removed successfully.');
  };

  // ─── Import Items from Uploaded CSV ───────────────────────────────────────
  const handleImportItems = async (newItems, mode = 'append') => {
    if (!isOrderInvoiced) {
      toast.warning('Adding medicines is only allowed when order status is INVOICED.');
      return;
    }
    if (!newItems || newItems.length === 0) return;
    const finalItems = mode === 'replace' ? newItems : [...items, ...newItems];
    setItems(finalItems);
    setSaveSuccessMessage(`Successfully imported ${newItems.length} items.`);
    setTimeout(() => setSaveSuccessMessage(''), 3500);

    if (currentOrder?.id) {
      await syncItemsToDb(finalItems);
    }
  };

  // ─── Print / Export PO ────────────────────────────────────────────────────
  const handlePrintPO = () => {
    window.print();
  };

  return (
    <div className="vt-po-detail-container">
      {/* ─── 1. Top Navigation & Header Bar: PO Number on Left, Back on Right ──── */}
      <div className="vt-po-nav-bar">
        <div className="vt-po-header-title-group">
          <h2 className="vt-po-main-title">
            Purchase Order <span className="vt-po-grn-highlight">{currentOrder.grnNo || 'NEW-PO'}</span>
          </h2>
          <span
            className={`vt-po-status-badge status-${String(currentOrder.status || 'INVOICED').toLowerCase().replace(/\s+/g, '_')}`}
            title="Current Order Status (Change status inside Preview)"
          >
            {currentOrder.status === 'PROCESSED_AND_RESTOCKED' || currentOrder.status === 'PROCESSED' || currentOrder.status === 'RESTOCKED' || currentOrder.status === 'PROCESSED AND RESTOCKED'
              ? 'PROCESSED AND RESTOCKED'
              : currentOrder.status || 'INVOICED'}
          </span>
          {statusAlertMessage ? (
            <span style={{ fontSize: '11px', color: '#16a34a', background: '#dcfce7', padding: '3px 10px', borderRadius: '12px', fontWeight: '600' }}>
              {statusAlertMessage}
            </span>
          ) : isUpdatingStatus ? (
            <span style={{ fontSize: '11px', color: '#0284c7', background: '#e0f2fe', padding: '3px 9px', borderRadius: '12px', fontWeight: '600' }}>
              Updating status...
            </span>
          ) : isSavingOrder ? (
            <span style={{ fontSize: '11px', color: '#0284c7', background: '#e0f2fe', padding: '3px 9px', borderRadius: '12px', fontWeight: '600' }}>
              Saving...
            </span>
          ) : saveSuccessMessage ? (
            <span style={{ fontSize: '11px', color: '#16a34a', background: '#dcfce7', padding: '3px 9px', borderRadius: '12px', fontWeight: '600' }}>
              ✓ {saveSuccessMessage}
            </span>
          ) : null}
        </div>

        <button type="button" className="vt-po-back-btn" onClick={onBack} title="Back to Purchase Entries">
          <FaArrowLeft size={13} /> Back to Purchase Orders
        </button>
      </div>

      {/* ─── 2. Top 3 Cards Row ───────────────────────────────────────────── */}
      <div className="vt-po-cards-grid">
        {/* Card 1: Total Items Stat Card */}
        <div className="vt-po-stat-card">
          <div className="vt-po-card-accent cyan-accent" />
          <div className="vt-po-card-icon-box">
            <FaCalendarAlt size={20} />
          </div>
          <div className="vt-po-stat-body">
            <div className="vt-po-stat-number">{items.length}</div>
            <div className="vt-po-stat-label">Total Items</div>
          </div>
        </div>

        {/* Card 2: Supplier Information Card */}
        <div className="vt-po-info-card">
          <div className="vt-po-info-card-header">
            <div className="vt-po-info-header-title">
              <FaTruck className="vt-po-icon-supplier" />
              <span>Supplier Information</span>
            </div>
            <button
              type="button"
              className="vt-po-mini-icon-btn"
              title="Edit Supplier"
              onClick={() => setIsEditSupplierModalOpen(true)}
            >
              <FaPen size={11} />
            </button>
          </div>

          <div className="vt-po-info-card-body">
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Supplier Name</span>
              <span className="vt-po-info-val highlight">{supplierDetails?.name || currentOrder.supplierName || '—'}</span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">GST No</span>
              <span className="vt-po-info-val">{supplierDetails?.gstNumber || 'N/A'}</span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">DL No1</span>
              <span className="vt-po-info-val">{supplierDetails?.dl1Number || supplierDetails?.dlNumber1 || 'N/A'}</span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">DL No2</span>
              <span className="vt-po-info-val">{supplierDetails?.dl2Number || supplierDetails?.dlNumber2 || 'N/A'}</span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Phone</span>
              <span className="vt-po-info-val">
                {supplierDetails?.phone1
                  ? (supplierDetails.phone2 ? `${supplierDetails.phone1}, ${supplierDetails.phone2}` : supplierDetails.phone1)
                  : (supplierDetails?.phone || supplierDetails?.mobile || 'N/A')}
              </span>
            </div>
          </div>
        </div>

        {/* Card 3: Created By / Order Metadata Card */}
        <div className="vt-po-info-card">
          <div className="vt-po-info-card-header">
            <div className="vt-po-info-header-title">
              <FaUser className="vt-po-icon-user" />
              <span>Created By</span>
            </div>
            <div className="vt-po-header-actions">
              {isOrderInvoiced && (
                <button
                  type="button"
                  className="vt-po-mini-icon-btn edit-accent"
                  onClick={handleOpenEditInvoiceModal}
                  title="Edit Invoice Details"
                >
                  <FaPen size={11} />
                </button>
              )}
            </div>
          </div>

          <div className="vt-po-info-card-body">
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Created By</span>
              <span className="vt-po-info-val">{currentOrder.representativeName || currentOrder.createdBy || 'Admin'}</span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Created On</span>
              <span className="vt-po-info-val">{formatDateTimeHuman(currentOrder.createdAt)}</span>
            </div>

            {/* When status is PROCESSED AND RESTOCKED: show Processed By and Processed On */}
            {(currentOrder.status === 'PROCESSED_AND_RESTOCKED' ||
              currentOrder.status === 'PROCESSED' ||
              currentOrder.status === 'RESTOCKED' ||
              currentOrder.status === 'PROCESSED AND RESTOCKED') && (
              <>
                <div className="vt-po-info-row vt-po-audit-row processed">
                  <span className="vt-po-info-lbl">Processed By</span>
                  <span className="vt-po-info-val highlight-user">
                    {currentOrder.processedBy || currentOrder.representativeName || currentUserName || 'Admin'}
                  </span>
                </div>
                <div className="vt-po-info-row vt-po-audit-row processed">
                  <span className="vt-po-info-lbl">Processed On</span>
                  <span className="vt-po-info-val highlight-date">
                    {formatDateTimeHuman(currentOrder.processedAt || currentOrder.updatedAt || currentOrder.createdAt)}
                  </span>
                </div>
              </>
            )}

            {/* When status is DELETED: show Deleted By and Deleted On */}
            {currentOrder.status === 'DELETED' && (
              <>
                <div className="vt-po-info-row vt-po-audit-row deleted">
                  <span className="vt-po-info-lbl">Deleted By</span>
                  <span className="vt-po-info-val highlight-user-del">
                    {currentOrder.deletedBy || currentOrder.representativeName || currentUserName || 'Admin'}
                  </span>
                </div>
                <div className="vt-po-info-row vt-po-audit-row deleted">
                  <span className="vt-po-info-lbl">Deleted On</span>
                  <span className="vt-po-info-val highlight-date-del">
                    {formatDateTimeHuman(currentOrder.deletedAt || currentOrder.updatedAt || currentOrder.createdAt)}
                  </span>
                </div>
              </>
            )}

            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Invoice No</span>
              <span className="vt-po-info-val mono">
                {currentOrder.invoiceNumber || '—'}
                {isOrderInvoiced && (
                  <button
                    type="button"
                    className="vt-po-inline-edit-btn"
                    onClick={handleOpenEditInvoiceModal}
                    title="Edit Invoice Number"
                  >
                    <FaPen size={9} />
                  </button>
                )}
              </span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Invoice Date</span>
              <span className="vt-po-info-val">
                {formatDateHuman(currentOrder.invoiceDate)}
                {isOrderInvoiced && (
                  <button
                    type="button"
                    className="vt-po-inline-edit-btn"
                    onClick={handleOpenEditInvoiceModal}
                    title="Edit Invoice Date"
                  >
                    <FaPen size={9} />
                  </button>
                )}
              </span>
            </div>
            <div className="vt-po-info-row">
              <span className="vt-po-info-lbl">Status</span>
              <span
                className={`vt-po-status-badge status-${String(currentOrder.status || 'INVOICED').toLowerCase().replace(/\s+/g, '_')}`}
                title="Current Order Status (Change status inside Preview)"
              >
                {currentOrder.status === 'PROCESSED_AND_RESTOCKED' || currentOrder.status === 'PROCESSED' || currentOrder.status === 'RESTOCKED' || currentOrder.status === 'PROCESSED AND RESTOCKED'
                  ? 'PROCESSED AND RESTOCKED'
                  : currentOrder.status || 'INVOICED'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── 3. Unified Master Items Card ─────────────────────────────────── */}
      <div className="vt-po-master-card">
        {/* Medicine Search & Actions Toolbar - only allowed when status is INVOICED */}
        {isOrderInvoiced ? (
          <div className="vt-po-toolbar-header">
            <div className="vt-po-toolbar-left">
              {/* Fused Brand / Composition / HSN Code Search Group */}
              <div className="vt-po-fused-search">
                <select
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="vt-po-search-select"
                >
                  <option value="Brand">Brand</option>
                  <option value="Composition">Composition</option>
                  <option value="HSN Code">HSN Code</option>
                </select>

                <div className="vt-po-search-input-wrapper" ref={searchWrapperRef}>
                  <input
                    type="text"
                    placeholder={`Search medicine by ${searchCategory.toLowerCase()}...`}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="vt-po-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="vt-po-clear-search-btn"
                      onClick={() => {
                        setSearchQuery('');
                        setShowSuggestions(false);
                      }}
                      title="Clear search"
                    >
                      <FaTimes size={11} />
                    </button>
                  )}

                  {/* ─── Autocomplete Suggestions Popover ─── */}
                  {showSuggestions && (
                    <div className="vt-po-suggestions-popover">
                      {/* Top Option: Add New Item */}
                      <div
                        className="vt-po-suggestion-add-new"
                        onClick={() => {
                          setShowSuggestions(false);
                          setIsCreateItemModalOpen(true);
                        }}
                      >
                        <div className="add-new-icon-box">
                          <FaPlus />
                        </div>
                        <div className="add-new-content">
                          <strong>+ Add {searchQuery.trim() ? `"${searchQuery.trim()}"` : 'New Medicine'} as new item</strong>
                          <span>Open Create new Item form with custom batch & GST</span>
                        </div>
                      </div>

                      {/* Suggestions matching current query */}
                      {filteredSuggestions.length > 0 ? (
                        <div className="vt-po-suggestions-list">
                          {filteredSuggestions.map((med) => (
                            <div
                              key={med.id}
                              className="vt-po-suggestion-row"
                              onClick={() => handleSelectSuggestion(med)}
                            >
                              <div className="sugg-main-info">
                                <span className="sugg-name">{med.itemName}</span>
                                {med.brand && <span className="sugg-brand-sub">{med.brand}</span>}
                              </div>
                              <div className="sugg-avail-box">
                                <span className="sugg-avail-chip">
                                  Available: <strong>{med.stockQuantity != null ? med.stockQuantity : (med.availableUnits != null ? med.availableUnits : (med.availableQuantity != null ? med.availableQuantity : 0))}</strong>
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="vt-po-suggestions-empty">
                          <span>No existing medicine found matching "{searchQuery}"</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="vt-po-reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setShowSuggestions(false);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="vt-po-toolbar-right">
              <button
                type="button"
                className="vt-po-upload-btn"
                onClick={() => setIsUploadModalOpen(true)}
                title="Upload items from CSV file"
              >
                <FaUpload size={12} /> Upload File
              </button>
              <button
                type="button"
                className={`vt-po-delete-rows-btn ${selectedRowIds.length > 0 ? 'has-selected' : ''}`}
                onClick={handleDeleteRows}
                title={selectedRowIds.length > 0 ? `Delete ${selectedRowIds.length} selected row(s)` : 'Select rows to delete'}
              >
                <FaTrash size={12} /> Delete Rows {selectedRowIds.length > 0 ? `(${selectedRowIds.length})` : ''}
              </button>
            </div>
          </div>
        ) : (
          <div className="vt-po-status-lock-notice">
            <div className="status-lock-left">
              <FaLock className="status-lock-icon" />
              <div>
                <strong className="status-lock-title">
                  Medicine Additions &amp; Modifications Locked ({currentOrder.status === 'PROCESSED_AND_RESTOCKED' || currentOrder.status === 'PROCESSED' || currentOrder.status === 'RESTOCKED' ? 'PROCESSED AND RESTOCKED' : (currentOrder.status || 'LOCKED')})
                </strong>
                <span className="status-lock-desc">
                  Adding or uploading medicines is only allowed when status is <strong>INVOICED</strong>. Since this purchase order is {currentOrder.status === 'PROCESSED_AND_RESTOCKED' || currentOrder.status === 'PROCESSED' || currentOrder.status === 'RESTOCKED' ? 'processed and restocked in inventory' : 'deleted'}, the item list is locked.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ─── 4. Items Data Table ─────────────────────────────────────────── */}
        <div className="vt-po-table-wrapper">
          <table className="vt-po-items-table">
            <thead>
              <tr>
                {/* 0. Row Selection Checkbox - only when INVOICED */}
                {isOrderInvoiced && (
                  <th style={{ minWidth: '42px', width: '42px', textAlign: 'center' }}>
                    <div className="vt-po-th-chip checkbox-th">
                      <input
                        type="checkbox"
                        className="vt-po-row-checkbox"
                        checked={items.length > 0 && selectedRowIds.length === items.length}
                        onChange={toggleSelectAll}
                        title="Select / Deselect all rows"
                      />
                    </div>
                  </th>
                )}

                {/* 1. Item Name */}
                <th style={{ minWidth: '180px', width: '16%' }}>
                  <div className="vt-po-th-chip">
                    <FaCapsules className="th-chip-icon" />
                    <span>Item Name</span>
                    <FaChevronDown className="th-chip-caret" />
                  </div>
                </th>

                {/* 2. Batch */}
                <th style={{ minWidth: '110px', width: '9%' }}>
                  <div className="vt-po-th-chip">
                    <FaBarcode className="th-chip-icon" />
                    <span>Batch</span>
                  </div>
                </th>

                {/* 3. Expiry */}
                <th style={{ minWidth: '110px', width: '9%' }}>
                  <div className="vt-po-th-chip">
                    <FaCalendarAlt className="th-chip-icon" />
                    <span>Expiry</span>
                  </div>
                </th>

                {/* 4. Pack quantity */}
                <th style={{ minWidth: '130px', width: '10%' }} title="no of units per pack">
                  <div className="vt-po-th-chip pack-qty-header" title="no of units per pack">
                    <FaBoxOpen className="th-chip-icon" />
                    <span>Pack quantity</span>
                    <span className="th-hover-tooltip">no of units per pack</span>
                  </div>
                </th>

                {/* 5. Quantity with Packs | Units toggle */}
                <th style={{ minWidth: '140px', width: '11%' }}>
                  <div className="vt-po-th-qty-stack">
                    <div className="vt-po-th-chip qty-chip">
                      <FaBoxes className="th-chip-icon" />
                      <span>Quantity</span>
                    </div>
                    <div className="vt-po-qty-toggle">
                      <button
                        type="button"
                        className={`qty-toggle-btn ${quantityMode === 'packs' ? 'active' : ''}`}
                        onClick={() => setQuantityMode('packs')}
                      >
                        Packs
                      </button>
                      <button
                        type="button"
                        className={`qty-toggle-btn ${quantityMode === 'units' ? 'active' : ''}`}
                        onClick={() => setQuantityMode('units')}
                      >
                        Units
                      </button>
                    </div>
                  </div>
                </th>

                {/* 6. Free */}
                <th style={{ minWidth: '90px', width: '7%' }}>
                  <div className="vt-po-th-chip">
                    <FaGift className="th-chip-icon" />
                    <span>Free</span>
                  </div>
                </th>

                {/* 7. MRP */}
                <th style={{ minWidth: '110px', width: '8%' }}>
                  <div className="vt-po-th-chip">
                    <FaArrowUp className="th-chip-icon" />
                    <span>MRP / Unit</span>
                  </div>
                </th>

                {/* 8. Rate */}
                <th style={{ minWidth: '115px', width: '9%' }}>
                  <div className="vt-po-th-chip">
                    <FaTag className="th-chip-icon" />
                    <span>Rate / Unit</span>
                  </div>
                </th>

                {/* 9. Discount */}
                <th style={{ minWidth: '100px', width: '7%' }}>
                  <div className="vt-po-th-chip">
                    <FaPercent className="th-chip-icon" />
                    <span>Discount</span>
                  </div>
                </th>

                {/* 10. % GST */}
                <th style={{ minWidth: '95px', width: '7%' }}>
                  <div className="vt-po-th-chip">
                    <FaReceipt className="th-chip-icon" />
                    <span>% GST</span>
                  </div>
                </th>

                {/* 11. Amount */}
                <th style={{ minWidth: '120px', width: '9%' }}>
                  <div className="vt-po-th-chip">
                    <FaCoins className="th-chip-icon" />
                    <span>Amount</span>
                  </div>
                </th>

                {/* 12. Actions */}
                <th style={{ minWidth: '85px', width: '6%' }}>
                  <div className="vt-po-th-chip">
                    <FaCog className="th-chip-icon" />
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={isOrderInvoiced ? 13 : 12} className="vt-po-empty-td">
                    <div className="vt-po-empty-state">
                      <p>No data Found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item, index) => {
                  const calc = calculateItemAmount(item);
                  const rowKey = item.id != null ? item.id : `idx-${index}`;
                  const isSelected = selectedRowIds.includes(rowKey);
                  return (
                    <tr
                      key={rowKey}
                      className={`vt-po-item-row ${isSelected ? 'row-selected' : ''}`}
                    >
                      {/* 0. Row Selection Checkbox - only when INVOICED */}
                      {isOrderInvoiced && (
                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="vt-po-row-checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectRow(rowKey)}
                            title="Select row"
                          />
                        </td>
                      )}

                      {/* 1. Item Name */}
                      <td className="item-name-cell">
                        <div className="vt-po-item-name-wrap">
                          <div className="vt-po-item-top-row">
                            <strong className="vt-po-item-title">{item.brand || item.itemName}</strong>
                            <span className="vt-po-weekly-badge">
                              <FaBolt size={8} /> Weekly {item.weeklyDemand || 23}
                            </span>
                          </div>
                          <div className="vt-po-item-bot-row">
                            <FaChartLine className="vt-po-trend-icon" />
                            <FaChevronRight className="vt-po-item-arrow" />
                          </div>
                        </div>
                      </td>

                      {/* 2. Batch */}
                      <td className="vt-po-batch-cell">{item.batchNo || '—'}</td>

                      {/* 3. Expiry */}
                      <td className="vt-po-expiry-cell">{formatExpiryMonthYear(item.expiryDate)}</td>

                      {/* 4. Pack Quantity */}
                      <td className="vt-po-packqty-cell">{item.packQuantity || 10}</td>

                      {/* 5. Quantity (Calculated by quantityMode) */}
                      <td className="vt-po-qty-cell">
                        {(() => {
                          const packQty = Math.max(1, Number(item.packQuantity) || 1);
                          const availUnits = item.availableUnits != null
                            ? Number(item.availableUnits)
                            : (item.stockQuantity != null ? Number(item.stockQuantity) : 0);

                          return (
                            <div className="vt-po-qty-stack">
                              <strong className="vt-po-qty-num">
                                {quantityMode === 'units'
                                  ? Number(item.quantity || 0) * packQty
                                  : Number(item.quantity || 0)}
                              </strong>
                              <div className="vt-po-avail-chip-row">
                                <span
                                  className="vt-po-avail-badge"
                                  title={`Available: ${availUnits} Units`}
                                >
                                  {availUnits}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </td>

                      {/* 6. Free (Calculated by quantityMode) */}
                      <td className="vt-po-free-cell">
                        {quantityMode === 'units'
                          ? Number(item.freeQuantity || 0) * (Number(item.packQuantity) || 1)
                          : Number(item.freeQuantity || 0)}
                      </td>

                      {/* 7. MRP */}
                      {(() => {
                        const grossPackMrp = Number(item.mrp || item.packMrp || 0);
                        let afterDiscountMrp = grossPackMrp;
                        if (item.sellingMrp != null && item.sellingMrp !== '' && Number(item.sellingMrp) > 0) {
                          afterDiscountMrp = Number(item.sellingMrp);
                        } else if (item.packMrpDiscount != null && Number(item.packMrpDiscount) > 0) {
                          const discVal = Number(item.packMrpDiscount);
                          if (item.packMrpDiscountType === '₹') {
                            afterDiscountMrp = Math.max(0, grossPackMrp - discVal);
                          } else {
                            afterDiscountMrp = Math.max(0, grossPackMrp - (grossPackMrp * discVal) / 100);
                          }
                        }

                        const packQty = Math.max(1, Number(item.packQuantity) || 1);
                        const unitMrp = afterDiscountMrp / packQty;
                        const origUnitMrp = grossPackMrp / packQty;

                        return (
                          <td className="vt-po-mrp-cell">
                            <div className="vt-po-mrp-stack">
                              <strong className="vt-po-mrp-val">
                                ₹ {unitMrp.toFixed(2)}
                              </strong>
                              {afterDiscountMrp < grossPackMrp && (
                                <span className="vt-po-mrp-orig" title="Original MRP / Unit before discount">
                                  ₹ {origUnitMrp.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })()}

                      {/* 8. Rate (Always per unit) */}
                      {(() => {
                        const qty = Number(item.quantity) || 0;
                        const freeQty = Number(item.freeQuantity) || 0;
                        const packQty = Math.max(1, Number(item.packQuantity) || 1);
                        const rawPackRate = Number(item.packRate != null ? item.packRate : item.rate) || 0;

                        // Rate per unit
                        const ratePerUnit = packQty > 0 ? (rawPackRate / packQty) : rawPackRate;

                        // 0.00 is change in rate per unit
                        const changeInRatePerUnit = Number(item.rateChangePerUnit != null
                          ? item.rateChangePerUnit
                          : (item.rateChange || 0));

                        // Net Unit Retail Price = purchase rate / (quantity + free quantity)
                        const totalUnits = (qty + freeQty) * packQty;
                        const totalPurchaseAmount = qty * rawPackRate;
                        const netUnitRetailPrice = item.netUnitRetailPrice != null
                          ? Number(item.netUnitRetailPrice)
                          : (totalUnits > 0 ? (totalPurchaseAmount / totalUnits) : ratePerUnit);

                        return (
                          <td className="vt-po-rate-cell">
                            <div className="vt-po-rate-stack">
                              <span className="vt-po-rate-val" title="Rate per unit">
                                ₹ {ratePerUnit.toFixed(2)}
                              </span>
                              {/* Sub-bar: Change in rate & Net unit retail price */}
                              <div className="vt-po-rate-sub-bar" title="[Change in Rate] - [Net Unit Price: purchase rate / (total units incl free)]">
                                <span className="rate-sub-prev" title="Change in Rate">
                                  ₹ {changeInRatePerUnit.toFixed(2)}
                                </span>
                                <span className="rate-sub-dash">-</span>
                                <span className="rate-sub-pill" title="Net Unit Price = Total Purchase / (Total Units incl. Free)">
                                  {netUnitRetailPrice.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </td>
                        );
                      })()}

                      {/* 9. Discount */}
                      <td className="vt-po-disc-cell">
                        {calc.discountAmount > 0 ? (
                          <div className="vt-po-disc-stack">
                            <span className="vt-po-disc-pct">
                              {item.discountType === '₹' || item.supplierDiscountType === '₹'
                                ? `₹ ${Number(item.discountValue != null ? item.discountValue : item.discountAmount).toFixed(2)}/pk`
                                : `${calc.discountPercent.toFixed(1)} %`}
                            </span>
                            <span className="vt-po-disc-val">- ₹ {calc.discountAmount.toFixed(2)}</span>
                          </div>
                        ) : (
                          <span className="vt-po-empty-dash">—</span>
                        )}
                      </td>

                      {/* 10. % GST */}
                      <td className="vt-po-gst-cell">{item.gstPercent || 0} %</td>

                      {/* 11. Amount (After discount, excluding GST) */}
                      <td className="vt-po-amount-cell">
                        <strong className="vt-po-amount-val" title="Amount after discount (excluding GST)">
                          ₹ {calc.taxableAmount.toFixed(2)}
                        </strong>
                      </td>

                      {/* 12. Actions */}
                      <td className="vt-po-actions-cell">
                        {isOrderInvoiced ? (
                          <div className="vt-po-action-btns-row">
                            <button
                              type="button"
                              className="vt-po-row-action-btn delete"
                              onClick={() => handleDeleteItem(item.id)}
                              title="Remove Item"
                            >
                              <FaTrash size={12} />
                            </button>
                            <button
                              type="button"
                              className="vt-po-row-action-btn edit"
                              onClick={() => {
                                setEditingItemIndex(index);
                                setEditingItemId(item.id != null ? item.id : null);
                                setSelectedMedicineForBatch(item);
                                setIsBatchDetailModalOpen(true);
                              }}
                              title="Edit Item Details"
                            >
                              <FaPen size={12} />
                            </button>
                          </div>
                        ) : (
                          <span className="vt-po-locked-pill" title="Order status is not Invoiced. Modifying items is locked.">
                            <FaLock size={10} /> Locked
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── 5. GST Breakdown & Order Summary Footer ──────────────────────── */}
      <div className="vt-po-footer-grid">
        {/* Left: GST Breakdown Box */}
        <div className="vt-po-gst-section">
          <div className="vt-po-gst-card">
            <div className="vt-po-gst-header">
              <FaCalculator className="gst-calc-icon" />
              <span>GST Breakdown</span>
            </div>

            <div className="vt-po-gst-table-wrapper">
              <table className="vt-po-gst-table">
                <thead>
                  <tr>
                    <th>CLASS</th>
                    <th>SUB TOTAL</th>
                    <th>DISCOUNT</th>
                    <th>SGST</th>
                    <th>CGST</th>
                    <th>TOTAL GST</th>
                  </tr>
                </thead>
                <tbody>
                  {gstBreakdown.map((row) => (
                    <tr key={row.rate}>
                      <td className="vt-po-gst-class-val">
                        <strong>GST {row.rate}%</strong>
                      </td>
                      <td>{formatCurrency(row.subtotal != null ? row.subtotal : row.taxable)}</td>
                      <td>{formatCurrency(row.discount || 0)}</td>
                      <td>{formatCurrency(row.sgst)}</td>
                      <td>{formatCurrency(row.cgst)}</td>
                      <td>{formatCurrency(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick Adjustment Buttons */}
          <div className="vt-po-adjustments-row">
            <button
              type="button"
              className={`vt-po-adj-btn ${discountAmount > 0 ? 'active' : ''}`}
              onClick={() => setIsBillDiscountModalOpen(true)}
              title={discountAmount > 0 ? 'Change Bill Discount' : 'Add Bill Discount'}
            >
              <FaPercent size={11} />{' '}
              {discountAmount > 0 ? `Change Discount (${formatCurrency(discountAmount)})` : 'Add Discount'}
            </button>

            <button
              type="button"
              className={`vt-po-adj-btn ${roundoffAmount !== 0 ? 'active' : ''}`}
              onClick={() => setIsRoundOffModalOpen(true)}
              title={roundoffAmount !== 0 ? 'Change Round Off' : 'Add Round Off'}
            >
              <FaCoins size={11} />{' '}
              {roundoffAmount !== 0
                ? `Change Round Off (${roundoffAmount >= 0 ? `+${formatCurrency(roundoffAmount)}` : formatCurrency(roundoffAmount)})`
                : hasDecimals
                ? `Add Round Off (${autoRoundoffDiff >= 0 ? '+' : ''}${autoRoundoffDiff.toFixed(2)})`
                : 'Add Round Off'}
            </button>

            <button
              type="button"
              className="vt-po-adj-btn"
              onClick={() => setIsAdjustmentInfoModalOpen(true)}
              title="Add Adjustment"
            >
              <FaCog size={11} /> Add Adjustment
            </button>
          </div>
        </div>

        {/* Right: Order Summary Box & Final CTAs */}
        <div className="vt-po-summary-section">
          <div className="vt-po-summary-card">
            <div className="vt-po-summary-row">
              <span className="summary-lbl" title="Gross subtotal without GST and without discount">Sub total</span>
              <span className="summary-val">{formatCurrency(subTotal)}</span>
            </div>

            {totalItemDiscount > 0 && (
              <div className="vt-po-summary-row discount">
                <span className="summary-lbl">Item Discount</span>
                <span className="summary-val">- {formatCurrency(totalItemDiscount)}</span>
              </div>
            )}

            {discountAmount > 0 && (
              <div className="vt-po-summary-row discount">
                <span className="summary-lbl">Bill Discount</span>
                <span className="summary-val">- {formatCurrency(discountAmount)}</span>
              </div>
            )}

            <div className="vt-po-summary-row">
              <span className="summary-lbl">Taxable Amount</span>
              <span className="summary-val">{formatCurrency(Math.max(0, taxableSubTotal - (Number(discountAmount) || 0)))}</span>
            </div>

            <div className="vt-po-summary-row">
              <span className="summary-lbl">SGST payable</span>
              <span className="summary-val">{formatCurrency(totalSGST)}</span>
            </div>
            <div className="vt-po-summary-row">
              <span className="summary-lbl">CGST payable</span>
              <span className="summary-val">{formatCurrency(totalCGST)}</span>
            </div>

            {roundoffAmount !== 0 && (
              <div className="vt-po-summary-row roundoff-row">
                <span className="summary-lbl">
                  Round off{' '}
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    ({roundoffAmount >= 0 ? `+${Number(roundoffAmount).toFixed(2)}` : Number(roundoffAmount).toFixed(2)})
                  </span>
                </span>
                <span
                  className="summary-val"
                  style={{
                    fontWeight: '600',
                    color: roundoffAmount >= 0 ? '#15803d' : '#b91c1c'
                  }}
                >
                  {roundoffAmount >= 0 ? `+ ${formatCurrency(roundoffAmount)}` : `- ${formatCurrency(Math.abs(roundoffAmount))}`}
                </span>
              </div>
            )}

            {/* Cyan / Blue Grand Total Bar */}
            <div className="vt-po-grand-total-bar">
              <span className="grand-lbl">Grand Total:</span>
              <span className="grand-val">{formatCurrency(grandTotal)}</span>
            </div>
          </div>

          {/* Action CTAs */}
          <div className="vt-po-final-actions">
            <button
              type="button"
              className="vt-po-preview-btn"
              onClick={() => setIsPreviewOpen(true)}
            >
              <FaEye /> Preview
            </button>
          </div>
        </div>
      </div>

      {/* ─── 6. Preview Modal ─────────────────────────────────────────────── */}
      {isPreviewOpen && (
        <div className="vt-po-modal-backdrop" onClick={() => setIsPreviewOpen(false)}>
          <div className="vt-po-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vt-po-preview-header">
              <div className="vt-po-preview-header-left">
                <FaPrint className="preview-header-icon" />
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px' }}>Purchase Order Preview</h3>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>Preview &amp; update order status</span>
                </div>
              </div>

              {/* Status Changing Option Inside Preview Modal Only */}
              <div className="vt-po-preview-status-box">
                <label className="vt-po-status-lbl">Status:</label>
                <select
                  className={`vt-po-status-select status-${String(currentOrder.status || 'INVOICED').toLowerCase().replace(/\s+/g, '_')}`}
                  value={
                    currentOrder.status === 'PROCESSED' || currentOrder.status === 'RESTOCKED' || currentOrder.status === 'PROCESSED_AND_RESTOCKED' || currentOrder.status === 'PROCESSED AND RESTOCKED'
                      ? 'PROCESSED_AND_RESTOCKED'
                      : currentOrder.status || 'INVOICED'
                  }
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={isUpdatingStatus}
                  title="Change Purchase Order Status: Invoiced, Processed and Restocked, Deleted"
                >
                  <option value="INVOICED">Invoiced</option>
                  <option value="PROCESSED_AND_RESTOCKED">PROCESSED AND RESTOCKED</option>
                  <option value="DELETED">Deleted</option>
                </select>
                {statusAlertMessage ? (
                  <span style={{ fontSize: '11px', color: '#16a34a', background: '#dcfce7', padding: '3px 8px', borderRadius: '10px', fontWeight: '600' }}>
                    {statusAlertMessage}
                  </span>
                ) : isUpdatingStatus ? (
                  <span style={{ fontSize: '11px', color: '#0284c7', background: '#e0f2fe', padding: '3px 8px', borderRadius: '10px', fontWeight: '600' }}>
                    Updating...
                  </span>
                ) : null}
              </div>

              <button
                type="button"
                className="vt-po-close-add-btn"
                onClick={() => setIsPreviewOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="vt-po-preview-body">
              <PurchaseOrderReceipt
                order={currentOrder}
                supplier={supplierDetails}
                items={items}
                discountAmount={discountAmount}
                roundoffAmount={roundoffAmount}
                effectiveRoundoff={effectiveRoundoff}
                taxableSubTotal={taxableSubTotal}
                grossBaseTotal={grossBaseTotal}
                totalSGST={totalSGST}
                totalCGST={totalCGST}
                gstBreakdown={gstBreakdown}
                billDiscountPct={billDiscountPct}
                grandTotal={grandTotal}
                calculateItemAmount={calculateItemAmount}
                formatDateHuman={formatDateHuman}
              />
            </div>
            <div className="vt-po-preview-footer">
              <button
                type="button"
                className="vt-po-btn-secondary"
                onClick={() => setIsPreviewOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="vt-po-btn-primary"
                onClick={handlePrintPO}
              >
                <FaPrint /> Print Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 7. Create New Item Modal ───────────────────────────────────── */}
      <CreateNewItemModal
        isOpen={isCreateItemModalOpen}
        onClose={() => setIsCreateItemModalOpen(false)}
        onSave={handleCreatedItemFromModal}
        initialSearchName={searchQuery}
      />

      {/* ─── 8. Medicine Batch Detail Confirmation Modal ─────────────────── */}
      <MedicineBatchDetailModal
        isOpen={isBatchDetailModalOpen}
        onClose={() => {
          setIsBatchDetailModalOpen(false);
          setSelectedMedicineForBatch(null);
          setEditingItemIndex(null);
          setEditingItemId(null);
        }}
        medicine={selectedMedicineForBatch}
        onConfirm={handleConfirmBatchDetail}
      />

      {/* ─── 9. Edit Supplier Modal (Only Supplier Name Editable) ─────── */}
      <EditPurchaseSupplierModal
        isOpen={isEditSupplierModalOpen}
        onClose={() => setIsEditSupplierModalOpen(false)}
        onSave={handleSaveSupplier}
        currentSupplier={supplierDetails || currentOrder}
        suppliersList={suppliers}
      />

      {/* ─── 10. Bill Discount Modal ─────────────────────────────────────── */}
      <BillDiscountModal
        isOpen={isBillDiscountModalOpen}
        onClose={() => setIsBillDiscountModalOpen(false)}
        onApply={handleApplyBillDiscount}
        currentDiscount={discountAmount}
        taxableSubTotal={taxableSubTotal}
        grossTotal={subTotal + totalSGST + totalCGST}
      />

      {/* ─── 11. Round Off Modal ─────────────────────────────────────────── */}
      <RoundOffModal
        isOpen={isRoundOffModalOpen}
        onClose={() => setIsRoundOffModalOpen(false)}
        onApply={handleApplyRoundOff}
        currentRoundOff={roundoffAmount}
        netBeforeRoundOff={netBeforeRoundOff}
      />

      {/* ─── 12. Add Adjustment Requirement Notice Modal ───────────────── */}
      {isAdjustmentInfoModalOpen && (
        <div className="vt-po-modal-backdrop" onClick={() => setIsAdjustmentInfoModalOpen(false)}>
          <div className="vt-adjustment-notice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vt-adjustment-notice-header">
              <div className="notice-icon-box">
                <FaInfoCircle size={18} />
              </div>
              <div className="notice-title-text">
                <h3>Add Adjustment</h3>
                <p>Feature Information</p>
              </div>
              <button
                type="button"
                className="vt-po-close-add-btn"
                onClick={() => setIsAdjustmentInfoModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="vt-adjustment-notice-body">
              <div className="notice-alert-box">
                <h4 style={{ margin: '0 0 6px 0', color: '#b45309' }}>Requirement Not Clear Yet</h4>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                  The requirements and business logic for the <strong>Add Adjustment</strong> feature are currently not clear yet.
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.4 }}>
                  Once the calculation rules (e.g. freight charges, handling fees, or custom deductions) are specified, this adjustment tool will be fully implemented.
                </p>
              </div>
            </div>
            <div className="vt-adjustment-notice-footer">
              <button
                type="button"
                className="vt-po-btn-primary"
                onClick={() => setIsAdjustmentInfoModalOpen(false)}
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 14. Edit Invoice Details Modal (Invoice Date & Number) ───────── */}
      {isEditInvoiceModalOpen && (
        <div className="vt-po-modal-backdrop" onClick={() => setIsEditInvoiceModalOpen(false)}>
          <div className="vt-po-edit-invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vt-po-edit-invoice-header">
              <div className="vt-po-edit-invoice-title">
                <FaFileInvoice className="header-icon" />
                <div>
                  <h3>Edit Invoice Details</h3>
                  <span>Update Invoice Number &amp; Date for this purchase order</span>
                </div>
              </div>
              <button
                type="button"
                className="vt-po-modal-close-btn"
                onClick={() => setIsEditInvoiceModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSaveInvoiceDetails}>
              <div className="vt-po-edit-invoice-body">
                <div className="vt-po-form-group">
                  <label>
                    <FaBarcode size={12} /> Invoice Number <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={editInvoiceForm.invoiceNumber}
                    onChange={(e) =>
                      setEditInvoiceForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))
                    }
                    placeholder="e.g. INV-2026-0042"
                    className="vt-po-form-input"
                  />
                </div>

                <div className="vt-po-form-group">
                  <label>
                    <FaCalendarAlt size={12} /> Invoice Date <span className="req">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={editInvoiceForm.invoiceDate}
                    onChange={(e) =>
                      setEditInvoiceForm((prev) => ({ ...prev, invoiceDate: e.target.value }))
                    }
                    className="vt-po-form-input"
                  />
                </div>
              </div>

              <div className="vt-po-edit-invoice-footer">
                <button
                  type="button"
                  className="vt-po-btn-secondary"
                  onClick={() => setIsEditInvoiceModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="vt-po-btn-primary"
                  disabled={isSavingInvoice}
                >
                  {isSavingInvoice ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Upload / Import Items Modal ─────────────────────────────────── */}
      <UploadItemsModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onImportItems={handleImportItems}
        existingItemsCount={items.length}
      />

      {/* ─── 13. Printable Order Sheet (Rendered during @media print) ───── */}
      <div id="vt-po-print-sheet" className="vt-po-print-sheet">
        <PurchaseOrderReceipt
          order={currentOrder}
          supplier={supplierDetails}
          items={items}
          discountAmount={discountAmount}
          roundoffAmount={roundoffAmount}
          effectiveRoundoff={effectiveRoundoff}
          taxableSubTotal={taxableSubTotal}
          grossBaseTotal={grossBaseTotal}
          totalSGST={totalSGST}
          totalCGST={totalCGST}
          gstBreakdown={gstBreakdown}
          billDiscountPct={billDiscountPct}
          grandTotal={grandTotal}
          calculateItemAmount={calculateItemAmount}
          formatDateHuman={formatDateHuman}
        />
      </div>
    </div>
  );
};

export default PurchaseOrderDetail;
